import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormBuilder,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  UserService,
  UserServiceState,
} from '../../../core/services/user.service';
import { Router, RouterLink } from '@angular/router';
import { SimService } from '../../../core/services/sim.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateFormatter } from '../../../utils/DateFormatter';
import { UserState } from '../../../core/models/user.model';
import { NotificationLayout } from '../../../shared/layout/notification-layout/notification-layout';
import { SimCardStatus } from '../../../core/models/sim.model';

@Component({
  selector: 'app-sim-activation',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    MatProgressSpinnerModule,
    NotificationLayout,
  ],
  templateUrl: './sim-activation.html',
  styleUrl: './sim-activation.scss',
})
export class SimActivation implements OnInit {
  dateFormatter = new DateFormatter();

  step: number = 1;

  simcardForm!: FormGroup;

  editMode: { [key: string]: boolean } = {
    iccd: false,
    puk: false,
  };

  @Output()
  cancelEmitter = new EventEmitter();

  currentUser: UserServiceState | null = null;

  error: null | string = null;

  isLoading = false;

  constructor(
    private userService: UserService,
    private simService: SimService,
    private router: Router,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.simcardForm = this.fb.group({
      iccd: ['', [Validators.required, Validators.pattern(/^[0-9]{19}$/)]],
      puk: ['', [Validators.required, Validators.pattern(/^[0-9]{8}/)]],
    });
  }

  handleNext(): void {
    let currentFieldControl;
    if (this.step === 1) {
      currentFieldControl = this.simcardForm.get('iccd');
    } else if (this.step === 2) {
      currentFieldControl = this.simcardForm.get('puk');
    }

    if (currentFieldControl) {
      currentFieldControl.markAsTouched(); 
      if (currentFieldControl.invalid) {
        return;
      }
    }

    this.step++; 
  }

  toggleEdit(field: string): void {
    this.editMode[field] = !this.editMode[field];
    if (this.editMode[field]) {
      setTimeout(() => {
        const inputElement = document.getElementById(`edit-${field}`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  }

  goToStep(newStep: number): void {
    this.step = newStep;
  }

  onSubmit(): void {
    this.error = null;
    this.simcardForm.markAllAsTouched();

    if (this.simcardForm.valid) {
      const iccd: string = `${this.simcardForm.get('iccd')?.value}`;
      const puk: string = `${this.simcardForm.get('puk')?.value}`;
      const activateUserData: UserState = {
        channel: 'WEB_PORTAL',
        identificationNumber: '',
        identificationType: '',
        email: '',
        iccid: iccd,
        identityVerified: false,
        isPersonalDataStorageAllowed: true,
        issueDate: '',
        profileType: 'digital',
        birthday: '',
      };
      if (activateUserData.birthday === null) {
        delete activateUserData.birthday;
      }
      this.isLoading = true;
      this.simService.getPhoneNumber(iccd).subscribe({
        next: (value) => {
          this.userService.setState({ phone: value.data.msisdn})
        }
      })
      this.simService.validatePhysicalSimcard({iccid: iccd, puk})
      .subscribe(({success, data}) => {
        if(success ){
          // Para pruebas: incluso si la SIM está marcada como ACTIVE, fuerza el flujo de activación.
          // Se setea el estado y se sigue a activate-form.
          this.userService.setState({ user:  activateUserData , iccd, puk, phone: 'force-flow' })
          this.isLoading = false;
          this.router.navigate(['activate-form'])
        }
      }, err => {
        if(!err.success){
          this.error = 'Error en los datos ingresados'
          this.router.navigate(['internal-error'])
          this.isLoading = false;
        }
      });
    } else {
      alert('Por favor, corrige los errores en el formulario.');
    }
  }

  getControl(controlName: string): FormControl {
    return this.simcardForm.get(controlName) as FormControl;
  }
}
